import { ReactElement, useCallback, useState, useEffect, useMemo, useRef } from 'react';

import { UploadResult } from 'firebase/storage';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useData } from '../../contextProviders/DataProvider';
import useLeagues from '../../dataHooks/useLeagues';
import Event from '../../types/Event';
import League, { LEAGUE_STATUSES } from '../../types/League';
import GratiImageUpload from '../common/GratiImageUpload';
import LeagueSelector from '../league/LeagueSelector';

import { FieldTarget } from './eventPreviewTypes';

export interface EventEssentialsEditorProps {
  event: Event;
  isNewEvent: boolean;
  /** Limit league choices to this org (set when the create page was opened from an org) */
  restrictToOrgId?: string;
  showOrgNames: boolean;
  onUpdate: (updates: Partial<Event>) => void;
  onLeagueChange: (league: League | undefined) => void;
  onImageUpdate: (snapshot: UploadResult | null) => void;
  onFileSelected?: (file: File | null) => void;
  /** Field to focus when the editor expands */
  focusTarget?: FieldTarget | null;
  /** Called after focus has been applied */
  onFocusComplete?: () => void;
}

export default function EventEssentialsEditor(props: EventEssentialsEditorProps): ReactElement {
  const { event, isNewEvent, restrictToOrgId, showOrgNames, onUpdate, onLeagueChange, onImageUpdate, onFileSelected, focusTarget, onFocusComplete } = props;
  const { getAdminLeagues, userState, activeMemberships } = useData();
  const [noLeague, setNoLeague] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const leagueInputRef = useRef<HTMLInputElement>(null);
  const orgInputRef = useRef<HTMLInputElement>(null);

  // Handle focus target from parent
  useEffect(() => {
    if (focusTarget === 'name' && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
      onFocusComplete?.();
    } else if (focusTarget === 'logo') {
      // Logo upload doesn't have a simple focus target, just acknowledge
      onFocusComplete?.();
    } else if (focusTarget === 'league') {
      // Focus the appropriate selector based on noLeague state
      if (noLeague && orgInputRef.current) {
        orgInputRef.current.focus();
      } else if (!noLeague && leagueInputRef.current) {
        leagueInputRef.current.focus();
      }
      onFocusComplete?.();
    }
  }, [focusTarget, onFocusComplete, noLeague]);

  // Get all admin leagues including catch-all ones
  const adminLeagueIds = getAdminLeagues();
  const { leagues: adminLeagues, isLeaguesLoading: isAdminLeaguesLoading } = useLeagues({
    includeOrgAdmined: true,
    includeLeagueAdmined: true,
    includeLeagueMember: false,
    includeCatchAll: true,
    orgId: restrictToOrgId,
    person: userState.status === 'ready' ? userState.userProfile : undefined,
    status: [LEAGUE_STATUSES.ACTIVE],
    providedMemberships: userState.status === 'ready' && activeMemberships ? {
      memberships: activeMemberships,
      isLoading: false,
      error: undefined,
    } : undefined,
  });

  // Filter to only catch-all leagues the user is admin of
  const catchAllLeagues = useMemo(() => {
    return adminLeagues.filter(league =>
      league.isCatchAll && adminLeagueIds.includes(league.itemId)
    );
  }, [adminLeagues, adminLeagueIds]);

  // User can use "No league" if they're admin of at least one catch-all league
  const canUseNoLeague = catchAllLeagues.length > 0;

  // With no real league to choose from, "No league" is the only option and stays checked
  const mustUseNoLeague =
    canUseNoLeague && !isAdminLeaguesLoading && !adminLeagues.some((league) => !league.isCatchAll);

  // When "No league" is checked and catch-all league changes
  const handleCatchAllLeagueChange = useCallback((leagueId: string) => {
    const league = catchAllLeagues.find(l => l.itemId === leagueId);
    if (league) {
      onLeagueChange({
        ...league,
        catchAllSeasonId: league.catchAllSeasonId,
      });
    }
  }, [catchAllLeagues, onLeagueChange]);

  // Start in "No league" mode when it's the only option, or when a new event was opened from a
  // catch-all league (e.g. /event-create/:orgId/:leagueId). Otherwise LeagueSelector, which hides
  // catch-all leagues, would be handed a value that isn't among its options.
  useEffect(() => {
    if (!noLeague && (mustUseNoLeague || catchAllLeagues.some((l) => l.itemId === event.leagueId))) {
      setNoLeague(true);
    }
  }, [noLeague, mustUseNoLeague, catchAllLeagues, event.leagueId]);

  // When "No league" is checked, auto-select the first catch-all league
  // (or the only one if single). This ensures we always have a valid value.
  useEffect(() => {
    if (noLeague && catchAllLeagues.length > 0) {
      // Check if current leagueId is already a catch-all league
      const currentIsCatchAll = catchAllLeagues.some(l => l.itemId === event.leagueId);
      if (!currentIsCatchAll) {
        handleCatchAllLeagueChange(catchAllLeagues[0].itemId);
      }
    }
  }, [noLeague, catchAllLeagues, event.leagueId, handleCatchAllLeagueChange]);

  const handleNoLeagueChange = (checked: boolean) => {
    setNoLeague(checked);
    if (!checked) {
      // When unchecking, clear the league so LeagueSelector can re-select
      onLeagueChange(undefined);
    }
  };

  // Get the selected catch-all league ID (only use event.leagueId if it's in catch-all list)
  const selectedCatchAllLeagueId = useMemo(() => {
    if (catchAllLeagues.some(l => l.itemId === event.leagueId)) {
      return event.leagueId;
    }
    return catchAllLeagues[0]?.itemId ?? '';
  }, [catchAllLeagues, event.leagueId]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          autoFocus
          id="name"
          name="event-name"
          label="Event name"
          autoComplete="off"
          inputRef={nameInputRef}
          slotProps={{ htmlInput: { 'data-1p-ignore': true } }}
          value={event.name}
          onChange={({ target }) => onUpdate({ name: target.value })}
          sx={{maxWidth: '20em'}}
        />
        <GratiImageUpload
          onImageUpdated={isNewEvent ? undefined : onImageUpdate}
          onFileSelected={isNewEvent ? onFileSelected : undefined}
          deferUpload={isNewEvent}
          isInitialized={!isNewEvent && Boolean(event.logo)}
          storageImageFileName={`${event.itemId}-logo`}
          storageImageLocation=
            {`/org/${event.orgId}/league/${event.leagueId}/event/${event.itemId}/orgMemberLeagueAdminFile`}
          label="Logo"
          isCompact
        />
      </Stack>
      {/* League selection with "No league" option for new events */}
      {isNewEvent ? (
        <Stack spacing={1}>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            {!noLeague ? (
              <LeagueSelector
                value={event.leagueId}
                orgId={restrictToOrgId}
                required
                onChange={onLeagueChange}
                showOrgNames={showOrgNames}
                showOnlyAdmined
                showCatchAll={false}
                disableIfSingleOption
                inputRef={leagueInputRef}
                sx={{ maxWidth: '20em' }}
              />
            ) : (
              // Show catch-all league selector by org name
              (<TextField
                select
                fullWidth
                label="Organization"
                value={selectedCatchAllLeagueId}
                disabled={catchAllLeagues.length <= 1}
                onChange={(e) => handleCatchAllLeagueChange(e.target.value)}
                inputRef={orgInputRef}
                sx={{ maxWidth: '20em' }}
              >
                {catchAllLeagues.map((league) => (
                  <MenuItem key={league.itemId} value={league.itemId}>
                    {league.orgName}
                  </MenuItem>
                ))}
              </TextField>)
            )}
            {canUseNoLeague && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={noLeague}
                    disabled={mustUseNoLeague}
                    onChange={(e) => handleNoLeagueChange(e.target.checked)}
                  />
                }
                label="No league"
                sx={{ whiteSpace: 'nowrap' }}
              />
            )}
          </Stack>
          {noLeague && (
            <Typography variant="caption" sx={{
              color: "text.secondary"
            }}>
              Event will be added to the organization&apos;s catch-all league.
            </Typography>
          )}
        </Stack>
      ) : (
        <TextField
          label="League"
          value={event.leagueName ?? ''}
          disabled
          helperText="League cannot be changed after event is created."
          sx={{ maxWidth: '20em' }}
        />
      )}
    </Stack>
  );
}